import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { api, ApiError } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { usePaged } from "@/lib/hooks";
import { PagedTable } from "@/components/PagedTable";
import { ConfirmButton } from "@/components/ConfirmButton";
import { CustomerForm } from "@/components/CustomerForm";
import { BookingStatusBadge, Button, Card, ErrorNote, Spinner, StatusBadge } from "@/components/ui";
import { useAuth } from "@/auth/auth";
import { tStatus } from "@/i18n";
import type { Booking, Customer } from "@/lib/types";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-sand-muted">{label}</dt>
      <dd className="mt-0.5 text-sm">{value || "—"}</dd>
    </div>
  );
}

import { formatCompanyDate } from "@/lib/time";

const col = createColumnHelper<Booking>();

export default function CustomerDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const canWrite = can("customers:write");
  const [editing, setEditing] = useState(false);
  const remove = useEntityAction([["customers"], ["customer", id]], {
    inlineError: true,
    successMessage: t("common.deleted"),
    onSuccess: () => navigate("/customers"),
  });
  const save = useEntityAction([["customers"], ["customer", id]], {
    inlineError: true,
    onSuccess: () => setEditing(false),
  });
  const customerQuery = useQuery({
    queryKey: ["customer", id],
    queryFn: () => api.get<Customer>(`/customers/${id}`),
  });
  const bookings = usePaged<Booking>(
    ["customer", id, "bookings"],
    `/customers/${id}/bookings`,
  );

  const bookingColumns = useMemo(
    () => [
      col.accessor("booking_number", {
        header: () => t("fields.number"),
        cell: (c) => (
          <Link className="font-medium text-sand hover:text-gold" to={`/bookings/${c.getValue()}`}>
            {c.getValue()}
          </Link>
        ),
      }),
      col.accessor("status", {
        header: () => t("fields.status"),
        cell: (c) => <BookingStatusBadge booking={c.row.original} />,
      }),
      col.accessor("currency", { header: () => t("fields.currency") }),
      col.accessor("created_at", {
        header: () => t("fields.created"),
        cell: (c) => formatCompanyDate(c.getValue()),
      }),
    ],
    [t],
  );

  if (customerQuery.isLoading) return <Spinner />;
  if (customerQuery.isError) {
    const err = customerQuery.error;
    return (
      <ErrorNote
        message={
          err instanceof ApiError && err.status === 404
            ? t("customers.notFound")
            : (err as Error).message
        }
      />
    );
  }

  const c = customerQuery.data!;
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/customers" className="text-sm text-sand-muted hover:text-gold">
          {t("customers.back")}
        </Link>
        <h1 className="text-lg font-semibold">{c.full_name}</h1>
        <StatusBadge status={c.status} />
        {canWrite && !editing && (
          <div className="ml-auto flex items-center gap-2">
            <Button className="px-3 py-1 text-xs" onClick={() => setEditing(true)}>
              {t("common.edit")}
            </Button>
            <ConfirmButton
              label={t("common.delete")}
              busy={remove.isPending}
              onConfirm={() => remove.mutate(() => api.del(`/customers/${id}`))}
            />
          </div>
        )}
      </div>
      {remove.isError && <ErrorNote message={(remove.error as Error).message} />}

      {editing ? (
        <CustomerForm
          initial={c}
          withStatus
          busy={save.isPending}
          error={save.isError ? (save.error as Error).message : null}
          submitLabel={t("common.save")}
          onCancel={() => setEditing(false)}
          onSubmit={(payload) =>
            save.mutate(() => api.patch(`/customers/${id}`, payload))
          }
        />
      ) : (
        <Card>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label={t("fields.displayName")} value={c.display_name} />
            <Field label={t("fields.company")} value={c.company} />
            <Field label={t("fields.country")} value={c.country} />
            <Field label={t("fields.nationality")} value={c.nationality} />
            <Field label={t("fields.language")} value={c.preferred_language} />
            <Field label={t("fields.emails")} value={c.emails.join(", ")} />
            <Field label={t("fields.phones")} value={c.phones.join(", ")} />
            <Field label={t("fields.tags")} value={c.tags.join(", ")} />
            <Field label={t("fields.source")} value={c.source ? tStatus(c.source) : "—"} />
            <Field label={t("fields.telegramId")} value={c.telegram_user_id?.toString()} />
          </dl>
          {c.notes && (
            <div className="mt-4">
              <Field label={t("fields.notes")} value={c.notes} />
            </div>
          )}
        </Card>
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold text-sand">{t("customers.bookings")}</h2>
        <PagedTable
          columns={bookingColumns}
          data={bookings.items}
          query={bookings.query}
          total={bookings.total}
          page={bookings.page}
          pageCount={bookings.pageCount}
          onPage={bookings.setPage}
          empty={t("customers.noBookings")}
        />
      </div>
    </div>
  );
}
